import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Ship, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { useAppAuth } from "@/auth/AppAuthProvider";

const SHIP_ASSET_TYPES = ["passenger_gangway", "baggage_door", "bunker_manifold", "shore_power", "freshwater", "waste", "cargo_door", "pilot_door", "medical"];
const TERMINAL_ASSET_TYPES = ["gangway", "humpy", "forklift", "crane", "baggage_cart", "bus", "service_vehicle", "bunker_hose", "shore_power_cable", "water_hose", "fender"];

export default function Vessels() {
  const { hasCapability } = useAppAuth();
  const canManage = hasCapability("reference.manage");
  const [vessels, setVessels] = useState([]);
  const [shipAssets, setShipAssets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [vOpen, setVOpen] = useState(false);
  const [sOpen, setSOpen] = useState(false);
  const [vForm, setVForm] = useState({ name: "", type: "cruise", imo: "", loa_m: 200, beam_m: 30, draft_m: 7, air_draft_m: 50, passenger_capacity: 1000, crew_capacity: 500, preferred_side: "starboard", notes: "" });
  const [sForm, setSForm] = useState({ vessel_id: "", name: "", type: "passenger_gangway", distance_from_bow_m: 50, side: "starboard", deck_level: 1, height_above_waterline_m: 5, freeboard_m: 0, clearance_m: 0, compatible_terminal_asset_types: ["gangway", "humpy"], notes: "" });

  const refresh = useCallback(async () => {
    const v = await api.list("vessels");
    setVessels(v);
    if (!selected && v.length) setSelected(v[0].id);
    if (selected) {
      const sa = await api.list("ship-assets", { vessel_id: selected });
      setShipAssets(sa);
    }
  }, [selected]);
  useEffect(() => { refresh(); }, [refresh]);

  const createVessel = async () => {
    if (!vForm.name) return toast.error("Name required");
    await api.create("vessels", vForm);
    setVOpen(false);
    toast.success("Vessel created");
    refresh();
  };

  const createShipAsset = async () => {
    if (!sForm.name || !selected) return toast.error("Name required");
    await api.create("ship-assets", { ...sForm, vessel_id: selected });
    setSOpen(false);
    toast.success("Ship asset created");
    refresh();
  };

  const removeShipAsset = async (id) => { await api.remove("ship-assets", id); refresh(); };

  const selV = vessels.find(v => v.id === selected);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Fleet</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">Vessels &amp; Ship Assets</h1>
        </div>
        <Dialog open={vOpen} onOpenChange={setVOpen}>
          {canManage && <DialogTrigger asChild>
            <Button data-testid={TESTIDS.btnCreateVessel} className="bg-sky-600 hover:bg-sky-500 rounded-none"><Plus size={14} className="mr-1.5" /> New Vessel</Button>
          </DialogTrigger>}
          <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-xl">
            <DialogHeader><DialogTitle className="font-heading">New vessel</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Name"><Input data-testid="input-vessel-name" value={vForm.name} onChange={e => setVForm({ ...vForm, name: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
              <Field label="Type">
                <Select value={vForm.type} onValueChange={v => setVForm({ ...vForm, type: v })}>
                  <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                    {["cruise", "cargo", "passenger", "ferry", "service", "naval", "research"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="IMO"><Input value={vForm.imo} onChange={e => setVForm({ ...vForm, imo: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              <Field label="Preferred side">
                <Select value={vForm.preferred_side} onValueChange={v => setVForm({ ...vForm, preferred_side: v })}>
                  <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                    <SelectItem value="port">port</SelectItem><SelectItem value="starboard">starboard</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {["loa_m", "beam_m", "draft_m", "air_draft_m", "passenger_capacity", "crew_capacity"].map(k => (
                <Field key={k} label={k}><Input type="number" step="0.1" value={vForm[k]} onChange={e => setVForm({ ...vForm, [k]: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
              ))}
            </div>
            <DialogFooter><Button onClick={createVessel} data-testid="btn-save-vessel" className="bg-sky-600 hover:bg-sky-500 rounded-none">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">Vessels</div>
          <div className="space-y-1.5">
            {vessels.map(v => (
              <button
                key={v.id}
                data-testid={`vessel-row-${v.id}`}
                onClick={() => setSelected(v.id)}
                className={`block w-full text-left p-2.5 transition-colors border-l-2 ${selected === v.id ? "bg-[#162032] border-sky-400" : "border-transparent hover:bg-[#0E1522]"}`}
              >
                <div className="flex items-center gap-2"><Ship size={14} className="text-sky-400" /><span className="text-sm font-medium">{v.name}</span></div>
                <div className="font-mono text-[10px] text-slate-500 mt-0.5">{v.type} · LOA {v.loa_m}m · draft {v.draft_m}m · IMO {v.imo || "—"}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 panel p-4">
          {selV && (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ship Assets for</div>
                  <div className="font-heading text-xl">{selV.name}</div>
                  <div className="font-mono text-[11px] text-slate-500">LOA {selV.loa_m}m · beam {selV.beam_m}m · draft {selV.draft_m}m</div>
                </div>
                <Dialog open={sOpen} onOpenChange={setSOpen}>
                  {canManage && <DialogTrigger asChild>
                    <Button data-testid={TESTIDS.btnCreateShipAsset} size="sm" className="bg-sky-600 hover:bg-sky-500 rounded-none"><Plus size={14} className="mr-1.5" /> Add Asset</Button>
                  </DialogTrigger>}
                  <DialogContent className="bg-[#0E1522] border border-[#1E293B] rounded-none text-slate-100 max-w-xl">
                    <DialogHeader><DialogTitle className="font-heading">New ship asset for {selV.name}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <Field label="Name"><Input data-testid="input-ship-asset-name" value={sForm.name} onChange={e => setSForm({ ...sForm, name: e.target.value })} className="bg-[#050B14] border-[#1E293B] rounded-none" /></Field>
                      <Field label="Type">
                        <Select value={sForm.type} onValueChange={v => setSForm({ ...sForm, type: v })}>
                          <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">{SHIP_ASSET_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </Field>
                      <Field label="distance_from_bow_m"><Input type="number" step="0.1" value={sForm.distance_from_bow_m} onChange={e => setSForm({ ...sForm, distance_from_bow_m: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                      <Field label="Side">
                        <Select value={sForm.side} onValueChange={v => setSForm({ ...sForm, side: v })}>
                          <SelectTrigger className="bg-[#050B14] border-[#1E293B] rounded-none"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
                            <SelectItem value="port">port</SelectItem><SelectItem value="starboard">starboard</SelectItem><SelectItem value="centerline">centerline</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="deck_level"><Input type="number" value={sForm.deck_level} onChange={e => setSForm({ ...sForm, deck_level: parseInt(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                      <Field label="height_above_waterline_m"><Input type="number" step="0.1" value={sForm.height_above_waterline_m} onChange={e => setSForm({ ...sForm, height_above_waterline_m: parseFloat(e.target.value) })} className="bg-[#050B14] border-[#1E293B] rounded-none font-mono" /></Field>
                      <Field label="Compatible terminal types" className="col-span-2">
                        <div className="flex flex-wrap gap-1.5">
                          {TERMINAL_ASSET_TYPES.map(t => {
                            const on = sForm.compatible_terminal_asset_types.includes(t);
                            return (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setSForm({ ...sForm, compatible_terminal_asset_types: on ? sForm.compatible_terminal_asset_types.filter(x => x !== t) : [...sForm.compatible_terminal_asset_types, t] })}
                                className={`text-[11px] font-mono px-2 py-1 ${on ? "sev-info" : "bg-[#050B14] border border-[#1E293B] text-slate-500"}`}
                              >{t}</button>
                            );
                          })}
                        </div>
                      </Field>
                    </div>
                    <DialogFooter><Button onClick={createShipAsset} data-testid="btn-save-ship-asset" className="bg-sky-600 hover:bg-sky-500 rounded-none">Create</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    <th className="text-left pb-2">Name</th><th className="text-left pb-2">Type</th><th className="text-left pb-2">From bow</th><th className="text-left pb-2">Side</th><th className="text-left pb-2">Deck</th><th className="text-left pb-2">Compatible</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {shipAssets.map(a => (
                    <tr key={a.id} className="border-b border-[#1E293B] hover:bg-[#0E1522]" data-testid={`ship-asset-row-${a.id}`}>
                      <td className="py-2">{a.name}</td>
                      <td className="font-mono py-2 text-slate-400">{a.type}</td>
                      <td className="font-mono py-2">{a.distance_from_bow_m}m</td>
                      <td className="font-mono py-2">{a.side}</td>
                      <td className="font-mono py-2">D{a.deck_level}</td>
                      <td className="font-mono py-2 text-[10px] text-slate-500">{(a.compatible_terminal_asset_types || []).join(", ")}</td>
                      <td className="text-right">{canManage && <button data-testid={`del-ship-asset-${a.id}`} onClick={() => removeShipAsset(a.id)} className="text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>}</td>
                    </tr>
                  ))}
                  {shipAssets.length === 0 && <tr><td colSpan={7} className="text-slate-500 py-3 text-sm">No ship assets defined.</td></tr>}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
